import { useTables } from '@/hooks';
import { ENCRYPT } from '@/constants';
import { openDB as idb, IDBPDatabase } from 'idb';
import pacote from '../../../package.json';
import { ProductProps, TableTypes, tableKeys } from '@/types';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

export type Tables = (typeof tableKeys)[number];
const listTables: Tables[] = [...tableKeys];

// localStorage.clear();

// // Limpar SessionStorage
// sessionStorage.clear();

// // Limpar IndexedDB
// indexedDB.databases().then(dbs => {
//   dbs.forEach(db => {
//     indexedDB.deleteDatabase(db.name || '');
//   });
// });

export const toCoded = (str: string, forceEncrypt?: boolean, noReplace?: boolean): string => {
  if (!str || (!ENCRYPT && !forceEncrypt)) return str;

  try {
    const filteredStr = noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(filteredStr);
    const binaryStr = String.fromCharCode(...uint8Array);
    return window.btoa(binaryStr).replace(/[^a-zA-Z]/g, '');
  } catch (error) {
    return noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
  }
};

const DB_NAME = toCoded('BATE_FORTE');
const DB_IMAGES_NAME = toCoded('BATE_FORTE_IMAGES');
const DB_VERSION = Number(pacote.version.replace('.', ''));
const RETRY_DELAY = 300;
const MAX_RETRIES = 5;

export const deleteDB = async (): Promise<void> => {
  try {
    await Promise.all([indexedDB.deleteDatabase(DB_NAME), new Promise((resolve) => setTimeout(resolve, 500))]);
  } catch (error) {
    console.error('Erro ao deletar banco de dados:', error);
  }
};

const createObjectStore = (db: IDBPDatabase, name: string): void => {
  if (name === 'imagens') return;

  const tableName = toCoded(name);
  if (!db.objectStoreNames.contains(tableName)) {
    db.createObjectStore(tableName, {
      keyPath: toCoded(name),
      autoIncrement: true,
    });
  }
};

const checkAndCreateMissingTables = async (): Promise<IDBPDatabase> => {
  let db = await idb(DB_NAME, DB_VERSION);
  const existingStores = Array.from(db.objectStoreNames);

  const missingTables = listTables.filter((tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName)));

  if (missingTables.length > 0) {
    db.close();

    const newVersion = listTables.length + (Math.floor(Date.now() / 1000) % 1000);

    db = await idb(DB_NAME, newVersion, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });
  }

  return db;
};

export const openDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    let db = await idb(DB_NAME, DB_VERSION, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });

    const existingStores = Array.from(db.objectStoreNames);
    const missingTables = listTables.filter(
      (tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName))
    );

    if (missingTables.length > 0) {
      db.close();
      db = await checkAndCreateMissingTables();
    }

    return db;
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de dados:', error);
    await deleteDB();
    await unregisterServiceWorker({});
    throw error;
  }
};

export const openImagesDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    return await idb(DB_IMAGES_NAME, DB_VERSION, {
      upgrade(db) {
        const storeName = toCoded('imagens');
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, {
            keyPath: storeName,
            autoIncrement: true,
          });
        }
      },
    });
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de imagens:', error);
    throw error;
  }
};

const handleGet = async <T extends Tables>(name: T, currDB: IDBPDatabase): Promise<TableTypes[T]> => {
  try {
    const transaction = currDB.transaction(toCoded(name), 'readonly');
    const store = transaction.objectStore(toCoded(name));
    const data = await store.getAll();
    const lastData = data[data.length - 1] || [];
    const resp = (await lastData.data) as TableTypes[T];
    return name === 'restricaoProdutoCliente' ? resp || [] : resp;
  } catch (error) {
    console.error(`Erro ao obter dados da tabela ${name}:`, error);
    return [] as TableTypes[T];
  }
};

export const useDbFunctions = () => {
  const { tables, setOnTables, isLoading } = useTables();

  const handleProductList = async <T extends Tables>(
    _: T,
    currDB: IDBPDatabase,
    COD_CLIENTE?: number
  ): Promise<TableTypes[T]> => {
    try {
      const [list, restrictedList] = await Promise.all([
        handleGet('produtos', currDB),
        handleGet('restricaoProdutoCliente', currDB),
      ]);

      const restrictions = new Set(
        restrictedList?.filter((r) => r?.COD_CLIENTE === COD_CLIENTE)?.map((r) => r?.COD_PRODUTO) || []
      );

      return list?.filter((p) => !restrictions.has(p.COD_PRODUTO)) as TableTypes[T];
    } catch (error) {
      console.error('Erro ao processar lista de produtos:', error);
      return [] as TableTypes[T];
    }
  };

  const closeDBConnection = (db: IDBPDatabase | undefined): void => {
    if (db) {
      db.close();
    }
  };

  const getImagesFromDB = async () => {
    try {
      const currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const data = await store.getAll();
      const lastData = data[data.length - 1] || [];
      closeDBConnection(currDB);
      return lastData.data;
    } catch (error) {
      console.error('Erro ao obter imagens do banco:', error);
      return null;
    }
  };

  const setImagesOnDB = async (data: any): Promise<void> => {
    if (!isValidData(data)) return;

    try {
      const currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readwrite');
      const store = transaction.objectStore(toCoded('imagens'));

      const allKeys = await store.getAllKeys();
      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]);
      }

      await store.put({ data, keyPath: toCoded('imagens') });
      await transaction.done;
      closeDBConnection(currDB);
    } catch (error) {
      console.error('Erro ao salvar imagens no banco:', error);
    }
  };

  const isValidData = (data: any): boolean => {
    return !!(
      data &&
      typeof data === 'object' &&
      !(Array.isArray(data) && data.length === 0) &&
      !(typeof data === 'object' && Object.keys(data).length === 0)
    );
  };

  const getOnDB = async <T extends Tables>(name: T, client?: number, retryCount: number = 0, force: boolean = false): Promise<TableTypes[T]> => {
    if (retryCount >= MAX_RETRIES && isLoading.current[name]) {
      throw new Error(`Tempo limite excedido ao carregar ${name}`);
    }

    if (isLoading.current[name] && !force) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return getOnDB(name, client, retryCount + 1);
    }

    isLoading.current[name] = true;

    try {
      const cachedData = tables.current?.[name];
      if (cachedData && !force) {
        isLoading.current[name] = false;
        return cachedData as TableTypes[T];
      }

      if (name === 'imagens') {
        const images = (await getImagesFromDB()) as TableTypes['imagens'];
        setOnTables(images, 'imagens', force);
        return images;
      }

      const currDB = await openDBConnection();
      let list = name === 'produtos' ? await handleProductList(name, currDB, client) : await handleGet(name, currDB);

      if (name === 'dicionario_produtos' && !list) {
        const produtos = await handleProductList('produtos', currDB, client);
        const dicionario = produtos.reduce(
          (acc, i) => ({
            ...acc,
            [i.COD_PRODUTO]: i,
          }),
          {} as Record<number, ProductProps>
        );
        list = dicionario as unknown as TableTypes[T];
      }

      setOnTables(list, name, force);
      return list as TableTypes[T];
    } catch (error) {
      console.error(`Erro ao obter dados de ${name}:`, error);
      return [] as TableTypes[T];
    } finally {
      isLoading.current[name] = false;
    }
  };

  const setOnDB = async (data: any, name: Tables): Promise<void> => {
    if (!isValidData(data) || !name) return;

    try {
      if (name === 'imagens') {
        return setImagesOnDB(data);
      }

      const currDB = await openDBConnection();
      const transaction = currDB.transaction(toCoded(name), 'readwrite');
      const store = transaction.objectStore(toCoded(name));

      const allKeys = await store.getAllKeys();
      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]);
      }

      await store.put({ data, keyPath: toCoded(name) });
      await transaction.done;
      closeDBConnection(currDB);
    } catch (error) {
      console.error(`Erro ao salvar dados em ${name}:`, error);
      return Promise.reject(error);
    }
  };

  const reloadTables = async () => {
    for (const table of listTables) {
      await getOnDB(table);
    }
  }

  return {
    getOnDB,
    setOnDB,
    deleteDB,
    reloadTables,
    closeDBConnection,
    getImagesFromDB,
    setImagesOnDB,
  };
};
